/** The admin's review of proposed companies, as decisions to apply. Pure for testing. */
import type { MigrationDecision, MigrationGroup } from "./types";

export type ReviewState = {
  /** Group id to the group it was merged into. */
  mergedInto: Record<string, string>;
  /** Root group ids the admin approved. */
  approved: Set<string>;
  /** Root group id to the company name to use. */
  names: Record<string, string>;
};

export const emptyReview = (): ReviewState => ({
  mergedInto: {},
  approved: new Set(),
  names: {}
});

export const rootOf = (state: ReviewState, id: string): string => {
  let at = id;
  const seen = new Set<string>();
  while (state.mergedInto[at] && !seen.has(at)) {
    seen.add(at);
    at = state.mergedInto[at];
  }
  return at;
};

export const merge = (
  state: ReviewState,
  a: string,
  b: string
): ReviewState => {
  const ra = rootOf(state, a);
  const rb = rootOf(state, b);
  if (ra === rb) return state;
  const approved = new Set(state.approved);
  if (approved.delete(rb)) approved.add(ra);
  return { ...state, mergedInto: { ...state.mergedInto, [rb]: ra }, approved };
};

export const split = (state: ReviewState, id: string): ReviewState => {
  const mergedInto = { ...state.mergedInto };
  delete mergedInto[id];
  Object.keys(mergedInto).forEach((k) => {
    if (mergedInto[k] === id) delete mergedInto[k];
  });
  return { ...state, mergedInto };
};

export type ProposedCompany = {
  root: MigrationGroup;
  groups: MigrationGroup[];
  caseCount: number;
  existingCompanyId: string | null;
};

export const proposedCompanies = (
  groups: MigrationGroup[],
  state: ReviewState
): ProposedCompany[] => {
  const byRoot = new Map<string, MigrationGroup[]>();
  groups.forEach((g) => {
    const root = rootOf(state, g.id);
    byRoot.set(root, [...(byRoot.get(root) ?? []), g]);
  });
  const ids = new Map(groups.map((g) => [g.id, g]));
  return [...byRoot.entries()]
    .map(([root, members]) => {
      const rootGroup = ids.get(root) ?? members[0];
      return {
        root: rootGroup,
        groups: [rootGroup, ...members.filter((m) => m.id !== rootGroup.id)],
        caseCount: members.reduce((n, m) => n + m.individual_ids.length, 0),
        existingCompanyId:
          members.map((m) => m.existing_company_id).find(Boolean) ?? null
      };
    })
    .sort(
      (a, b) =>
        b.caseCount - a.caseCount || a.root.name.localeCompare(b.root.name)
    );
};

export const decisionsFor = (
  groups: MigrationGroup[],
  state: ReviewState
): MigrationDecision[] =>
  proposedCompanies(groups, state)
    .filter((p) => state.approved.has(p.root.id))
    .map((p) => ({
      group_ids: p.groups.map((g) => g.id),
      ...(p.existingCompanyId ? { company_id: p.existingCompanyId } : {}),
      ...(state.names[p.root.id]?.trim()
        ? { name: state.names[p.root.id].trim() }
        : {})
    }));
